import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Search, BookOpen, Smartphone } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.azalega-academy.com';
const API_URL = API_BASE.endsWith('/api/v1') ? API_BASE : `${API_BASE}/api/v1`;

const TradingviewMobilePage = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchCourses();
    }, []);

    const fetchCourses = async () => {
        try {
            // Fetch all published courses
            const response = await axios.get(`${API_URL}/courses`, {
                params: {
                    limit: 100,
                    published: true
                }
            });

            const fetchedCourses = response.data.courses || response.data || [];

            // Filter for relevant courses (tradingview + mobile)
            const relevantCourses = fetchedCourses.filter(course =>
            (course.category === 'tradingview' && (
                course.videoSubcategory === 'mobile' ||
                course.videoSubcategory === 'handphone' ||
                course.tags?.includes('mobile') ||
                course.title?.toLowerCase().includes('mobile') ||
                course.title?.toLowerCase().includes('handphone')
            ))
            );

            // Map to display format
            const mappedCourses = relevantCourses.map(course => ({
                id: course._id,
                name: course.title,
                description: course.shortDescription || course.description,
                lessons: course.totalLessons || course.modules?.reduce((acc, m) => acc + (m.lessons?.length || 0), 0) || 0,
                duration: course.totalDuration ? `${Math.floor(course.totalDuration / 60)} min` : 'N/A',
                route: `/courses/${course._id}`,
                thumbnail: course.thumbnail,
                label: 'Modul Handphone'
            }));

            setCourses(mappedCourses);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching courses:', error);
            setLoading(false);
        }
    };

    const filteredCourses = courses.filter(course =>
        course.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-black">
            {/* Header Section */}
            <div className="bg-[#ffffff] dark:bg-black border-b border-gray-200 dark:border-gray-800 py-8 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
                        <Link to="/student/dashboard" className="hover:text-gray-900 dark:hover:text-white transition-colors">Dashboard</Link>
                        <span className="mx-2">›</span>
                        <Link to="/my-courses" className="hover:text-gray-900 dark:hover:text-white transition-colors">Courses</Link>
                        <span className="mx-2">›</span>
                        <span className="text-gray-900 dark:text-white font-medium">Tradingview Mobile</span>
                    </div>

                    <div className="flex items-center mb-6">
                        <div className="bg-purple-500 p-3 rounded-xl mr-4">
                            <Smartphone size={28} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">Modul Handphone</h1>
                            <p className="text-gray-600 dark:text-gray-400">
                                Trade on the go with complete mobile Tradingview tutorials.
                            </p>
                        </div>
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gray-50 dark:!bg-black rounded-lg p-4 border border-gray-200 dark:border-gray-800">
                            <div className="flex items-center">
                                <div className="bg-purple-500 p-2 rounded-lg mr-3">
                                    <BookOpen size={18} className="text-white" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{courses.length}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Total Courses</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search & Filter Section */}
            <div className="bg-[#ffffff] dark:bg-black border-b border-gray-200 dark:border-gray-800 py-6 px-6 sticky top-0 z-10 shadow-sm">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search mobile courses..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 dark:text-white bg-white dark:bg-black placeholder-gray-500"
                            />
                        </div>
                        <div className="md:w-64">
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 dark:text-white bg-white dark:bg-black"
                            >
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="name">Name (A-Z)</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Courses Grid */}
            <div className="max-w-7xl mx-auto px-6 py-8">
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
                        <p className="text-gray-500 dark:text-gray-400 mt-4">Loading courses...</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredCourses.map((course) => (
                                <Link
                                    key={course.id}
                                    to={course.route}
                                    className="group bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden hover:border-purple-500 hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-300"
                                >
                                    {/* Card Header with Icon/Thumbnail */}
                                    <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                                        <div className="flex items-center justify-between mb-4">
                                            {course.thumbnail ? (
                                                <div className="w-16 h-16 rounded-xl shadow-lg overflow-hidden group-hover:scale-110 transition-transform bg-[#ffffff] dark:bg-black">
                                                    <img
                                                        src={course.thumbnail.startsWith('http') ? course.thumbnail : `${API_URL.replace('/api/v1', '')}${course.thumbnail}`}
                                                        alt={course.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="bg-purple-500 w-16 h-16 rounded-xl flex items-center justify-center text-3xl shadow-lg group-hover:scale-110 transition-transform text-white">
                                                    <Smartphone size={32} />
                                                </div>
                                            )}
                                        </div>

                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-purple-600 transition-colors">
                                            {course.name}
                                        </h3>

                                        <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2">
                                            {course.description}
                                        </p>
                                    </div>

                                    {/* Card Footer */}
                                    <div className="p-6 bg-gray-50 dark:bg-black group-hover:bg-blue-50 dark:group-hover:bg-black transition-colors">
                                        <div className="flex items-center justify-between text-sm mb-4">
                                            <div className="flex items-center text-gray-600 dark:text-gray-400">
                                                <BookOpen size={16} className="mr-1" />
                                                <span>{course.lessons} lessons</span>
                                            </div>
                                            <div className="flex items-center text-gray-600 dark:text-gray-400">
                                                <span className="mr-1">⏱️</span>
                                                <span>{course.duration}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-500 dark:text-gray-500">View Details</span>
                                            <span className="text-purple-600 dark:text-purple-400 font-semibold group-hover:text-purple-700 dark:group-hover:text-purple-300 text-sm">
                                                Start Learning →
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>

                        {/* No Results */}
                        {!loading && filteredCourses.length === 0 && (
                            <div className="text-center py-12">
                                <div className="text-gray-400 mb-2">
                                    <Search size={48} className="mx-auto" />
                                </div>
                                <p className="text-gray-600 dark:text-gray-400 text-lg">
                                    No courses found matching "{searchQuery}"
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default TradingviewMobilePage;
